/**
 * Mock API service for Tenant Profile.
 * Uses localStorage to simulate backend persistence.
 *
 * When the real backend is ready, replace the mock implementations
 * with actual AWS Amplify API calls:
 *   GET    /users/{userId}/profile       -> getOwnProfile
 *   POST   /users/{userId}/profile       -> saveProfile
 *   DELETE /users/{userId}/profile/slug  -> revokeSlug
 *   POST   /users/{userId}/profile/slug  -> regenerateSlug
 *   GET    /p/{slug}                     -> getPublicProfile
 */
import { TenantProfile, TenantProfileData, PublicTenantProfile } from '../types/tenantProfile';

const STORAGE_PREFIX = 'leea_tenant_profile_';
const SLUG_INDEX_KEY = 'leea_tenant_profile_slugs';

/** Generate a crypto-random URL-safe slug (128 bits / 22 chars) */
const generateSlug = (): string => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // base64url encode
    let binary = '';
    bytes.forEach(b => { binary += String.fromCharCode(b); });
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/** Simulate network latency */
const delay = (ms = 300): Promise<void> => new Promise(r => setTimeout(r, ms));

/** Get slug -> userId index from localStorage */
const getSlugIndex = (): Record<string, string> => {
    try {
        return JSON.parse(localStorage.getItem(SLUG_INDEX_KEY) || '{}');
    } catch {
        return {};
    }
};

/** Update slug -> userId index */
const setSlugIndex = (index: Record<string, string>) => {
    localStorage.setItem(SLUG_INDEX_KEY, JSON.stringify(index));
};

/** Get profile from localStorage */
const getStoredProfile = (userId: string): TenantProfile | null => {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/** Save profile to localStorage */
const storeProfile = (profile: TenantProfile) => {
    localStorage.setItem(`${STORAGE_PREFIX}${profile.userId}`, JSON.stringify(profile));
    // Update slug index
    const index = getSlugIndex();
    // Remove old slug if any
    Object.keys(index).forEach(slug => {
        if (index[slug] === profile.userId) delete index[slug];
    });
    // Add current slug
    index[profile.slug] = profile.userId;
    setSlugIndex(index);
};

/** Strip sensitive data for public view */
const toPublicProfile = (profile: TenantProfile): PublicTenantProfile => {
    const { language, ...publicData } = profile.data;
    return {
        name: profile.data.name,
        data: publicData,
        profilePictureUrl: profile.data.profilePicture || undefined,
        language,
        identityVerified: profile.identityVerified,
        bankDataVerified: profile.bankDataVerified
    };
};

// --- Mock API methods ---

const getOwnProfile = async (userId: string): Promise<TenantProfile> => {
    await delay();
    const profile = getStoredProfile(userId);
    if (!profile) {
        const error: any = new Error('Profile not found');
        error.response = { status: 404 };
        throw error;
    }
    return profile;
};

const saveProfile = async (userId: string, data: TenantProfileData): Promise<TenantProfile> => {
    await delay(400);
    const existing = getStoredProfile(userId);
    const now = Date.now();

    const profile: TenantProfile = existing
        ? {
            ...existing,
            data,
            updatedAt: now
        }
        : {
            userId,
            slug: generateSlug(),
            isActive: true,
            data,
            identityVerified: false,
            bankDataVerified: false,
            createdAt: now,
            updatedAt: now
        };

    storeProfile(profile);
    return profile;
};

const revokeSlug = async (userId: string): Promise<{ success: boolean; isActive: boolean }> => {
    await delay();
    const profile = getStoredProfile(userId);
    if (!profile) {
        throw new Error('Profile not found');
    }
    profile.isActive = false;
    profile.updatedAt = Date.now();
    storeProfile(profile);
    return { success: true, isActive: false };
};

const regenerateSlug = async (userId: string): Promise<{ slug: string; isActive: boolean }> => {
    await delay();
    const profile = getStoredProfile(userId);
    if (!profile) {
        throw new Error('Profile not found');
    }
    // Remove old slug from index
    const index = getSlugIndex();
    delete index[profile.slug];
    setSlugIndex(index);

    profile.slug = generateSlug();
    profile.isActive = true;
    profile.updatedAt = Date.now();
    storeProfile(profile);
    return { slug: profile.slug, isActive: true };
};

const getPublicProfile = async (slug: string): Promise<PublicTenantProfile> => {
    await delay(200);
    const index = getSlugIndex();
    const userId = index[slug];
    if (!userId) {
        const error: any = new Error('Profile not found');
        error.response = { status: 404 };
        throw error;
    }
    const profile = getStoredProfile(userId);
    if (!profile || !profile.isActive) {
        const error: any = new Error('Profile not found');
        error.response = { status: 404 };
        throw error;
    }
    return toPublicProfile(profile);
};

export default {
    getOwnProfile,
    saveProfile,
    revokeSlug,
    regenerateSlug,
    getPublicProfile
};
